import React, { useLayoutEffect } from 'react';
import {
    AnimationUtils,
    BufferGeometry,
    EdgesGeometry,
    Group,
    LineBasicMaterial,
    LineSegments,
    Mesh,
    MeshStandardMaterial,
    Object3D,
    WireframeGeometry
} from 'three';
import { useLoader, useThree } from '@react-three/fiber';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { isEnglish } from '../../../utils/helper/helpers';
import { boardConfig } from '../../../config/boardConfig';
import { getBoardAssetPosition, getColor } from '../../../utils/helper/3d/helper';
import { useGlobal } from '../../../logic/main';
import { useAnimations } from '@react-three/drei';
import { Material } from 'three/src/materials/Material';
import { transformAnimations } from '../../../utils/helper/animationHelper';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls';
import { CustomColors } from '../../../styles/variables';

export const Szene: React.FC = () => {
    const [
        _,
        { setCameraInstance, setAnimations, toggleOrbitControls, setCurrenAssetTransformation, setIsTransforming }
    ] = useGlobal(
        _ => ({}),
        action => ({
            setCameraInstance: action.setCameraInstance,
            setAnimations: action.setAnimations,
            setCurrenAssetTransformation: action.setCurrenAssetTransformation,
            setIsTransforming: action.setIsTransforming,
            toggleOrbitControls: action.toggleOrbitControls
        })
    );
    const obj = useLoader(GLTFLoader, (isEnglish() ? '../' : '') + 'media/3d/szene.glb');
    const { actions } = useAnimations(obj.animations, obj.scene);
    const { gl, scene, camera } = useThree();

    const adaptMaterial = (mesh: Mesh<BufferGeometry, Material>, root: Object3D) => {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        const id = mesh.name;
        mesh.material = mesh.material.clone() as MeshStandardMaterial;
        mesh.material.transparent = true;
        mesh.material.opacity = boardConfig.maxOpacity;
        mesh.material.depthWrite = true;

        if (id === 'human' || id === 'human_body') {
            /* const geo = new WireframeGeometry(mesh.geometry); // or WireframeGeometry
            const scale = 0.270305;
            geo.scale(scale, scale, scale);
            const mat = new LineBasicMaterial({
                color: getColor(CustomColors.vertices),
                opacity: 0,
                transparent: true
            });
            const wireframe = new LineSegments(geo, mat);
            mesh.add(wireframe);*/
            /* (mesh.material as any).wireframe = true;*/
        }

        if (
            !!boardConfig.opacityAdjustmentList.find(
                i =>
                    i.toLowerCase().includes(id.toLowerCase()) ||
                    (mesh.parent?.name && i.toLowerCase().includes(mesh.parent.name.toLowerCase()))
            ) ||
            id.startsWith('highlight')
        ) {
            mesh.material.opacity = 0;
            if (id.startsWith('highlight')) {
                mesh.renderOrder = 1;
                mesh.material.depthWrite = false;
                mesh.material.depthTest = true;
            }
        }

        boardConfig.assets.push({
            id,
            position: getBoardAssetPosition(mesh.position, root.scale.z),
            ref: mesh as Mesh<BufferGeometry, MeshStandardMaterial>
        });
    };

    const enableDebugMode = (mesh: Mesh<BufferGeometry, Material>) => {
        //console.log('Trying to enable debug for:', mesh);
        if (mesh.parent?.name === 'Scene') {
            const control = new TransformControls(camera, gl.domElement);
            let lastMeshTransformations = { position: { ...mesh.position }, quaternion: { ...mesh.quaternion } };

            control.addEventListener('dragging-changed', function(event) {
                //console.log('mesh.position', mesh.position, 'event.value', event.value);
                toggleOrbitControls(!event.value);
                if (event.value) {
                    setIsTransforming(true);
                    lastMeshTransformations = { position: { ...mesh.position }, quaternion: { ...mesh.quaternion } };
                } else {
                    setIsTransforming(false);
                    setCurrenAssetTransformation({
                        position: mesh.position,
                        quaternion: mesh.quaternion,
                        name: mesh.name
                    });
                }
            });

            document.onkeydown = (event: KeyboardEvent) => {
                switch (event.key) {
                    case 'r':
                        control.mode = 'rotate';
                        break;
                    case 't':
                        control.mode = 'translate';
                        break;
                    case 's':
                        control.mode = 'scale';
                        break;
                    case 'z':
                        control.reset();
                        mesh.position.set(
                            lastMeshTransformations.position.x,
                            lastMeshTransformations.position.y,
                            lastMeshTransformations.position.z
                        );
                        mesh.quaternion.set(
                            lastMeshTransformations.quaternion.x,
                            lastMeshTransformations.quaternion.y,
                            lastMeshTransformations.quaternion.z,
                            lastMeshTransformations.quaternion.w
                        );
                        break;
                    default:
                        control.mode = 'translate';
                        break;
                }
            };

            control.attach(mesh);
            scene.add(control);
        }
    };

    const initialiseMeshes = (obj: Group) => {
        obj.traverse(child => {
            if (child instanceof Mesh) {
                adaptMaterial(child, obj);
                if (boardConfig.debug) {
                    enableDebugMode(child);
                }
            }
        });
        // console.log('BOARD ASSETS:', boardConfig.assets);
    };

    useLayoutEffect(() => {
        if (obj) {
            // console.log('Loaded Szene Object:', obj);
            const finalAnimations = transformAnimations(
                Object.keys(actions).length > 0 ? (actions as any) : obj.animations
            );
            // console.log('Loaded Animations:', finalAnimations, Object.keys(actions).length);
            const subClips = obj.animations.map(a => {
                let subClip = a;
                try {
                    //TODO: jeweils nach boardConfig.frames loopen und nochmal subclipen am Ende flatten!
                    subClip = AnimationUtils.subclip(a, a.name, 0, 10);
                } catch (e) {}
                return subClip;
            });
            // console.log(subClips);

            setCameraInstance(obj.cameras[0]);
            boardConfig.animations = finalAnimations;
            setAnimations(finalAnimations);
            obj.scene.castShadow = true;
            obj.scene.receiveShadow = true;
            initialiseMeshes(obj.scene);
        }
    }, [obj, actions]);

    return <primitive object={obj.scene} scale={1} />;
};
